import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageProvider";
import { Layout } from "@/components/layout/Layout";
import { Toaster } from "@/components/ui/sonner";
import { PAGES } from "@/lib/routes";

import { AnalyticsBootstrap } from "@/analytics/AnalyticsBootstrap";
const Home = lazy(() => import("@/pages/Home"));
const About = lazy(() => import("@/pages/About"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const ConditionDetail = lazy(() => import("@/pages/ConditionDetail"));
const Publications = lazy(() => import("@/pages/Publications"));
const Practice = lazy(() => import("@/pages/Practice"));
const Articles = lazy(() => import("@/pages/Articles"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Book = lazy(() => import("@/pages/Book"));
const Contact = lazy(() => import("@/pages/Contact"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
import { PanelAuthProvider } from "@/panel/AuthContext";
import { ProtectedRoute } from "@/panel/ProtectedRoute";

const PanelLogin = lazy(() => import("@/pages/panel/PanelLogin"));
const PanelDashboard = lazy(() => import("@/pages/panel/PanelDashboard"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));

const Cancellation = () => <LegalPage docKey="cancellation" />;
const Payment = () => <LegalPage docKey="payment" />;
const Privacy = () => <LegalPage docKey="privacy" />;
const Terms = () => <LegalPage docKey="terms" />;
const Cookies = () => <LegalPage docKey="cookies" />;
const HomeMain = () => <Home variant="tropical" />;
const HomeClassic = () => <Home variant="classic" />;

const COMPONENTS = {
  home: HomeMain,
  homeClassic: HomeClassic,
  about: About,
  serviceDetail: ServiceDetail,
  conditionDetail: ConditionDetail,
  publications: Publications,
  practice: Practice,
  articles: Articles,
  faq: FAQ,
  book: Book,
  contact: Contact,
  cancellation: Cancellation,
  payment: Payment,
  privacy: Privacy,
  terms: Terms,
  cookies: Cookies,
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <PanelAuthProvider>
            <AnalyticsBootstrap />
            <Suspense fallback={<div data-testid="page-loading" className="min-h-screen bg-ivory" />}>
            <Routes>
              <Route path="/bird" element={<PanelLogin />} />
              <Route path="/bird/dashboard" element={<ProtectedRoute><PanelDashboard /></ProtectedRoute>} />
              <Route element={<Layout />}>
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
                {PAGES.flatMap((p) => {
                  const El = COMPONENTS[p.key];
                  if (!El) return [];
                  return [
                    <Route key={`${p.key}-el`} path={p.el} element={<El />} />,
                    <Route key={`${p.key}-en`} path={p.en} element={<El />} />,
                  ];
                })}
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            </Suspense>
          </PanelAuthProvider>
          <Toaster position="top-center" richColors />
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
